// "Add tip": card alerts carry the pre-tip amount, so a tip is added on top of the bank charge. The
// same control in three places: the edit sheet and the add sheet (fields with ids, read on save)
// and each New purchases row (state in main.ts ui.newTips, above "Split this bill"). Quick picks
// of 15, 18 and 20% of the charge, a tip typed in dollars, or the final total. "Total $49.68"
// shows before anything is saved. Maths and copy in shared/tips.ts.
import type { OverviewDto, TransactionDto } from '../../../shared/api';
import { TIP_ADD, TIP_ADD_PROMPT, TIP_BELOW_CHARGE, TIP_NONE, TIP_PERCENTS, TIP_USUALLY, tipFor, tipLikely, tipSmallPrint, tipTotalLine } from '../../../shared/tips';
import { esc, money } from '../format';

/** A New purchases row's tip (main.ts ui.newTips): absent is closed, no tip. */
export interface NewTip {
  /** null: nothing valid typed yet (or a total below the charge). */
  tipCents: number | null;
  tipped: boolean;
  /** The final total typed is below the charge. */
  below?: boolean;
  /** The Edit sheet's Tip switch is off: no tip (any tip the purchase had is taken off on Confirm). */
  off?: boolean;
}

const setAsideIds = (d: OverviewDto) => new Set(d.categories.filter((k) => k.kind === 'set_aside').map((k) => k.id));

/** Tips are for my own spending: never a Done entry, money set aside or my share of their split. */
export function canTip(d: OverviewDto, t: TransactionDto | null, categoryId: string | null = t?.categoryId ?? null): boolean {
  if (t && (t.source === 'transfer' || t.filedBy === 'split' || t.split?.direction === 'received')) return false;
  return !(categoryId && setAsideIds(d).has(categoryId));
}

/** "Add tip?" up front, or a quiet "Add tip" link (shared/tips.ts tipLikely()). */
export const tipProminent = (d: OverviewDto, t: Pick<TransactionDto, 'usuallyTipped' | 'bankText'> | null, categoryId: string | null, typed?: string) =>
  tipLikely({
    usuallyTipped: t?.usuallyTipped,
    categoryName: d.categories.find((k) => k.id === categoryId)?.name ?? null,
    bankText: t?.bankText ?? typed ?? null,
  });

const dollarsField = (cents: number | null) => (cents == null ? '' : (cents / 100).toFixed(2));

/** The opener: a button when a tip is likely, else a link. */
export const tipOpener = (prominent: boolean, attrs: string) =>
  prominent ? `<button class="btn tipopen" ${attrs}>${TIP_ADD_PROMPT}</button>` : `<button class="linkbtn tipopen" ${attrs}>${TIP_ADD}</button>`;

/** The total line: "Total $49.68", or why not. */
export function tipLine(chargeCents: number | null, tip: { tipCents: number | null; below?: boolean }): string {
  if (tip.below) return TIP_BELOW_CHARGE;
  if (!chargeCents) return 'Enter the amount above to see the total.';
  return tipTotalLine(chargeCents, tip.tipCents ?? 0);
}

/** Quick picks: "15%" ... pressed when the tip is exactly that percent of the charge. */
const pctChips = (chargeCents: number | null, tipCents: number | null, attrs: (pct: number) => string) =>
  `<div class="chips tipchips">${TIP_PERCENTS.map(
    (p) => `<button class="chip" ${attrs(p)} aria-pressed="${!!chargeCents && tipCents != null && tipCents === tipFor(chargeCents, p)}">${p}%</button>`,
  ).join('')}</div>`;

/**
 * The edit or add sheet's tip: closed (the opener) unless the purchase has one. `chargeCents` is
 * the amount in the sheet (null while nothing is typed in the add sheet).
 */
export function sheetTipBlock(o: { chargeCents: number | null; tipCents: number | null; usuallyTipped: boolean; prominent: boolean }): string {
  const open = o.tipCents != null;
  const total = o.chargeCents ? o.chargeCents + (o.tipCents ?? 0) : null;
  return `<div class="tipbox" id="f-tip-box">
  <span id="f-tip-opener"${open ? ' hidden' : ''}>${tipOpener(o.prominent, 'data-a="tip-open"')}</span>
  <div id="f-tip-fields"${open ? '' : ' hidden'}>
  ${pctChips(o.chargeCents, o.tipCents, (p) => `data-a="tip-pct" data-pct="${p}"`)}
  <div class="tipgrid"><label class="fld"><span>Tip</span><div class="tipamt"><b>$</b><input id="f-tip" type="text" inputmode="decimal" autocomplete="off" placeholder="0.00" value="${dollarsField(o.tipCents)}"></div></label>
  <label class="fld"><span>Final total</span><div class="tipamt"><b>$</b><input id="f-total" type="text" inputmode="decimal" autocomplete="off" placeholder="0.00" value="${dollarsField(open ? total : null)}"></div></label></div>
  <p class="hint tiptotal" id="f-tip-total">${esc(tipLine(o.chargeCents, { tipCents: o.tipCents }))}</p>
  <div class="tipfoot"><label class="check"><input type="checkbox" id="f-tipped"${o.usuallyTipped ? ' checked' : ''}> ${TIP_USUALLY}</label><button class="linkbtn" data-a="tip-clear">${TIP_NONE}</button></div>
  </div></div>`;
}

/**
 * A New purchases row's tip, above its "Split this bill": the opener while closed; open, the same
 * picks and fields as the sheet, kept in ui.newTips.
 */
export function newTipBlock(d: OverviewDto, t: TransactionDto, tip: NewTip | undefined): string {
  if (!canTip(d, t)) return '';
  const id = esc(t.id);
  if (!tip) return `<div class="tipbox newtip">${tipOpener(tipProminent(d, t, t.categoryId), `data-a="new-tip-open" data-id="${id}"`)}</div>`;
  const total = tip.tipCents != null ? t.amountCents + tip.tipCents : null;
  return `<div class="tipbox newtip">
  ${pctChips(t.amountCents, tip.tipCents, (p) => `data-a="new-tip-pct" data-id="${id}" data-pct="${p}"`)}
  <div class="tipgrid"><label class="fld"><span>Tip</span><div class="tipamt"><b>$</b><input type="text" inputmode="decimal" autocomplete="off" placeholder="0.00" value="${dollarsField(tip.tipCents)}" data-new-tip="${id}" data-charge="${t.amountCents}"></div></label>
  <label class="fld"><span>Final total</span><div class="tipamt"><b>$</b><input type="text" inputmode="decimal" autocomplete="off" placeholder="0.00" value="${dollarsField(total)}" data-new-total="${id}" data-charge="${t.amountCents}"></div></label></div>
  <p class="hint tiptotal" data-new-tip-total="${id}">${esc(tipLine(t.amountCents, tip))}</p>
  <div class="tipfoot"><label class="check"><input type="checkbox" data-new-tipped="${id}"${tip.tipped ? ' checked' : ''}> ${TIP_USUALLY}</label></div>
  </div>`;
}

/** The total a New purchases row adds up to: the charge plus its tip (what a split shares). */
export const rowTotal = (t: TransactionDto, tip: NewTip | undefined) => t.amountCents + (tip?.tipCents ?? 0);

/** Under a tipped purchase's total (Activity rows and the edit sheet): "Bank charged $42.10, tip $7.58". */
export const tipRowLine = (t: TransactionDto) => (t.tipCents ? `<span class="tsplit">${esc(tipSmallPrint(t.amountCents, t.tipCents))}</span>` : '');

/** The edit sheet's header for a tipped purchase that is not split: the total, then the bank charge. */
export const tipSheetAmount = (t: TransactionDto) => (t.tipCents && !t.split ? `<p class="sheetamt"><b>${money(t.countedCents, 2)}</b> ${tipRowLine(t)}</p>` : '');
